import { useState, useContext, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Modal,
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { useMutation } from "@apollo/client";
import { SET_PASSWORD } from "../../Mutations/SetPassword";
import { UserContext } from "../../Contexts/UserContext";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { ModalHeader } from "../ModalHeader";
import { validatePassword } from "../../utils/validatePassword/validatePassword";
// import { validatePassword } from "password-validation";

const useStyles = makeStyles(theme => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    // padding: theme.spacing(2, 4, 3),
    display: "flex",
    flexDirection: "column",
  },
  paddingContainer: {
    padding: "15px",
    width: "25vw",
    height: "75vh",
    overflow: "auto",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "auto",
  },
  padding: {
    paddingBottom: "30px",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  hidden: {
    display: "none",
  },
  text: {
    fontSize: "1rem",
    color: "red",
    textWrap: "wrap",
  },
}));

//To do:
//handle errors from setPassword mutation

const ChangePassword = props => {
  const { showPasswordChange, closePasswordChange, loginCallback } = props;

  let { login, currentPassword, firstName, lastName, badge, userId } =
    props || "";

  const { user, nightMode } = useContext(UserContext);
  const [currentPasswordValue, setCurrentPasswordValue] = useState("");
  const [newPasswordValue, setNewPasswordValue] = useState("");
  const [verifyPasswordValue, setVerifyPasswordValue] = useState("");
  const [passwordResponse, setPasswordResponse] = useState({});
  // const isValid = Object.values(validation).every(validationField => validationField?.success === true);
  const isValid =
    passwordResponse?.isValid && newPasswordValue === verifyPasswordValue;

  const classes = useStyles();

  if (user?.id) {
    login = user?.officer?.deptAttribute?.login;
    firstName = user?.firstName;
    lastName = user?.lastName;
    badge = user?.badge;
    userId = user?.id;
  }
  useEffect(() => {
    if (currentPassword) setCurrentPasswordValue(currentPassword);
  }, [currentPassword]);

  const [setPasswordMutation] = useMutation(SET_PASSWORD);

  const updateState = e => {
    const name = e.target.name;
    const value = e.target.value;
    switch (name) {
      case "currentPasswordValue":
        setCurrentPasswordValue(value);
        break;
      case "newPasswordValue":
        setNewPasswordValue(value);
        break;
      case "verifyPasswordValue":
        setVerifyPasswordValue(value);
        break;
      case "passwordResponse":
        setPasswordResponse(value);
        break;
      default:
        alert("Invalid input");
        break;
    }
  };

  useEffect(() => {
    let res = validatePassword(newPasswordValue, {
      username: login,
      firstName: firstName,
      lastName: lastName,
      badgeNumber: badge,
    });
    if (newPasswordValue !== verifyPasswordValue) {
      res.errors.push("Passwords do not match");
    }
    updateState({ target: { name: "passwordResponse", value: res } });
  }, [
    newPasswordValue,
    verifyPasswordValue,
    user,
    login,
    firstName,
    lastName,
    badge,
  ]);

  const handleSubmit = async e => {
    e.preventDefault();
    let input = {
      originatorId: userId,
      newPassword: newPasswordValue,
      currentPassword: currentPasswordValue,
    };
    const res = await setPasswordMutation({
      variables: {
        input,
      },
    });
    const errors = res?.data?.setPassword?.errors || [];
    const success = errors?.length === 0;
    let message = success
      ? `Your password has been changed.`
      : errors.join("\n");
    alert(message);
    if (success) {
      clearFields();
      closePasswordChange();
      if (loginCallback) loginCallback();
    }
  };

  const clearFields = () => {
    updateState({ target: { name: "newPasswordValue", value: "" } });
    updateState({ target: { name: "verifyPasswordValue", value: "" } });
  };

  const handleClose = () => {
    clearFields();
    closePasswordChange();
  };

  return (
    <Modal
      aria-labelledby="change-password-modal"
      aria-describedby="change-password-modal-description"
      open={showPasswordChange}
      onClose={handleClose}
      className={classes.modal}
    >
      <form
        data-cy="change-password-form"
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <div className={classes.paper}>
          <ModalHeader
            nightMode={nightMode}
            headerName={"Change Your Password"}
          />
          <div id="padding-container" className={classes.paddingContainer}>
            <PasswordInput
              name="currentPasswordValue"
              className={classes.padding}
              label="Current Password"
              value={currentPasswordValue}
              onChange={updateState}
              helperText="Please enter your current password here"
            />
            <PasswordInput
              name="newPasswordValue"
              className={classes.padding}
              label="New Password"
              value={newPasswordValue}
              onChange={updateState}
              helperText="Please enter your new password here"
            />
            <PasswordInput
              name="verifyPasswordValue"
              className={classes.padding}
              label="Verify Password"
              value={verifyPasswordValue}
              onChange={updateState}
              helperText="Please re-enter your new password here"
            />
            <ValidationErrors errorsArray={passwordResponse?.errors} />
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-around"
            >
              <Button
                data-cy="password-submit-button"
                color="primary"
                variant="contained"
                disabled={!isValid}
                type="submit"
              >
                Submit
              </Button>
              <Button
                data-cy="password-cancel-button"
                color="primary"
                variant="contained"
                onClick={handleClose}
              >
                Cancel
              </Button>
            </Box>
          </div>
        </div>
      </form>
    </Modal>
  );
};

const ValidationErrors = ({ errorsArray = [] }) => {
  return (
    <div>
      {errorsArray?.map((error, index) => {
        return (
          <p
            key={error + index}
            style={{ fontSize: "1rem", color: "red", textWrap: "wrap" }}
          >
            {error}
          </p>
        );
      })}
    </div>
  );
};

const PasswordInput = ({
  name,
  className,
  label,
  value,
  onChange,
  helperText,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <TextField
      autoComplete="off"
      id="password-text-field"
      type={showPassword ? "text" : "password"}
      name={name}
      className={className}
      label={label}
      value={value}
      onChange={onChange}
      helperText={helperText}
      variant="outlined"
      fullWidth={true}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip
              title={showPassword ? "Hide Password" : "Reveal Password"}
              aria-label="show-hide-password"
            >
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(prevState => !prevState)}
                onMouseDown={e => e.preventDefault()}
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default ChangePassword;
